import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button, Col, Empty, Row, Select, Table } from "antd";
import { CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons";

import { useFetchConstant } from "apis/constant";
import {
  useGetAdviseeProposalReporting,
  useGetAdviseeProposals,
} from "apis/advisee";
import {
  constructShortenedFinancialNumber,
  getLabelOfConstant,
  guard,
} from "utils/general";
import { getReportingData } from "utils/reporting";
import { buildPNRProposalsUri } from "utils/routes";

import Title from "components/Title/Title";
import SectionContainer from "components/SectionContainer/SectionContainer";
import SummaryInfoCard from "components/SummaryInfoCard/SummaryInfoCard";

import {
  CoverageLabel,
  CoveragePriceText,
  CoverageYearText,
  CardContainer,
  ExtraInfoText,
  FinancialTitleText,
  FinancialValueText,
  Label,
  Selection,
} from "./Summary.styles";

import Locale from "react-intl-universal";

const { OptGroup, Option } = Select;

const DiffIcon = ({ beforeValue, afterValue }) => {
  const before = Number(String(beforeValue).replace(/[^0-9.-]/g, ""));
  const after = Number(String(afterValue).replace(/[^0-9.-]/g, ""));

  if (before > after) {
    return <CaretDownOutlined style={{ color: "#EC373A", marginLeft: 4 }} />;
  } else if (after > before) {
    return <CaretUpOutlined style={{ color: "#2AC940", marginLeft: 4 }} />;
  } else {
    return null;
  }
};

const getRiskRatioTableColumns = (title) => [
  {
    title,
    dataIndex: "label",
    key: "label",
    width: "25%",
    render: (value) => <Label>{value}</Label>,
  },
  {
    title: (
      <FinancialTitleText>
        {Locale.get("proposal.beforeOptimization")}
      </FinancialTitleText>
    ),
    dataIndex: "value",
    key: "value",
    width: "15%",
    render: (value, rec) => (
      <FinancialValueText isInRange={rec.isInRange}>{value}</FinancialValueText>
    ),
  },
  {
    title: (
      <FinancialTitleText>
        {Locale.get("proposal.afterOptimization")}
      </FinancialTitleText>
    ),
    dataIndex: "optimisedValue",
    key: "optimisedValue",
    width: "15%",
    render: (value, rec) => ({
      children: (
        <FinancialValueText isInRange={rec.isOptimisedInRange}>
          {value}
          <DiffIcon beforeValue={rec.value} afterValue={value} />
        </FinancialValueText>
      ),
      props: {
        style: { background: "#f1fcff" },
      },
    }),
  },
  {
    title: (
      <FinancialTitleText>
        {Locale.get("proposal.currentAgeOptimal")}
      </FinancialTitleText>
    ),
    dataIndex: "range",
    key: "range",
    width: "20%",
    render: (value, rec) => <FinancialValueText>{value}</FinancialValueText>,
  },
  {
    title: Locale.get("proposal.note"),
    dataIndex: "desc",
    key: "desc",
    width: "25%",
    render: (value) => <ExtraInfoText>{value}</ExtraInfoText>,
  },
];

const GrossCoverageCard = ({ data = {}, order, title }) => {
  return (
    <>
      <Col span={12} lg={8} order={order}>
        <CardContainer>
          <CoverageLabel>{title}</CoverageLabel>
          <CoveragePriceText>
            {data.lifeInsNeeds
              ? constructShortenedFinancialNumber(data.lifeInsNeeds, false)
              : "-"}
          </CoveragePriceText>
          <CoverageYearText>
            {Locale.get("proposal.for")} <b>{data.yearsOfCoverage || "-"}</b>{" "}
            {Locale.get("proposal.year")}
          </CoverageYearText>
        </CardContainer>
      </Col>
    </>
  );
};

const RiskRatioTable = ({ dataSource, columns }) => {
  return (
    <Row>
      <Col span={24}>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          rowKey={(record) => record.label}
          style={{ width: "100%" }}
          scroll={{ x: 432 }}
        />
      </Col>
    </Row>
  );
};

const ProposalSelection = ({
  proposalsByStatus,
  value,
  onChange,
  proposalStatuses,
}) => {
  return (
    <span style={{ float: "right" }}>
      <b>{Locale.get("proposal.proposal")}</b>
      <Selection value={value} onChange={onChange}>
        {Object.entries(proposalsByStatus).map(
          ([proposalStatus, proposals]) => (
            <OptGroup
              label={getLabelOfConstant(proposalStatus, proposalStatuses)}
            >
              {proposals.map((proposal) => (
                <Option value={proposal._id}>{proposal.name}</Option>
              ))}
            </OptGroup>
          )
        )}
      </Selection>
    </span>
  );
};

const Summary = ({
  personalInfo,
  currentReporting,
  setIsSettingsModalVisible,
}) => {
  const history = useHistory();
  const { id: adviseeId } = useParams();
  const [selectedProposal, setSelectedProposal] = useState();

  const { data: proposalReporting } = useGetAdviseeProposalReporting(
    adviseeId,
    selectedProposal
  );
  const { data: proposals } = useGetAdviseeProposals(adviseeId);
  const { selection: proposalStatuses } = useFetchConstant("proposalStatuses");

  const { riskRatioData } = currentReporting;
  const {
    riskRatioData: proposedRiskRatioData,
    grossInsNeeds,
  } = proposalReporting;

  const {
    naturalDeathCoverageData,
    accidentalDeathCoverageData,
    medicalBenefitCoverageData,
    tpdCoverageData,
    comatoseCoverageData,
  } = getReportingData(riskRatioData, proposedRiskRatioData);

  console.log(riskRatioData, naturalDeathCoverageData, 139);

  useEffect(() => {
    if (Object.values(proposals).length) {
      const proposalFirstOption = guard(
        () =>
          Object.values(proposals).filter((proposal) => proposal.length)[0][0]
            ._id
      );
      setSelectedProposal(proposalFirstOption);
    }
  }, [proposals]);

  const handleOnCreateProposalButtonClick = () => {
    history.push(buildPNRProposalsUri(adviseeId));
    setIsSettingsModalVisible(true);
  };

  return (
    <>
      {selectedProposal ? (
        <div>
          <Title>{Locale.get("proposal.summary")}</Title>
          <SummaryInfoCard
            name={personalInfo.name}
            age={personalInfo.age}
            dob={personalInfo.dob}
            occupation={personalInfo.occupation}
            dependentYears={personalInfo.minYearsOfDependency}
            noOfDependentFamilyMember={personalInfo.noOfDependentFamilyMember}
          />
          <Title marginBottom="8px">
            {Locale.get("proposal.grossInsuranceNeed")}
          </Title>
          <SectionContainer>
            <Row gutter={[24, 16]}>
              <GrossCoverageCard
                order={1}
                data={grossInsNeeds && grossInsNeeds.deathCover}
                title={Locale.get("proposal.deathCoverage")}
              />
              <GrossCoverageCard
                order={2}
                data={grossInsNeeds && grossInsNeeds.tpd}
                title={Locale.get("proposal.tpdCoverage")}
              />
              <GrossCoverageCard
                order={3}
                data={grossInsNeeds && grossInsNeeds.ci}
                title={Locale.get("proposal.ciCoverage")}
              />
            </Row>
          </SectionContainer>
          <SectionContainer>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: "8px" }}
            >
              <Col span={12}>
                <Title marginBottom="8px">
                  {Locale.get("proposal.riskRatio")}
                </Title>
              </Col>
              <Col span={12}></Col>
            </Row>

            <Row
              justify="end"
              style={{
                position: "sticky",
                top: 40,
                zIndex: 100,
                transform: "translate(0px, -58px)",
              }}
            >
              <ProposalSelection
                proposalsByStatus={proposals}
                value={selectedProposal}
                onChange={(value) => setSelectedProposal(value)}
                proposalStatuses={proposalStatuses}
              />
            </Row>

            <div>
              <RiskRatioTable
                dataSource={naturalDeathCoverageData}
                columns={getRiskRatioTableColumns(
                  Locale.get("proposal.naturalDeathCoverage")
                )}
              />
              <RiskRatioTable
                dataSource={accidentalDeathCoverageData}
                columns={getRiskRatioTableColumns(
                  Locale.get("proposal.accidentalDeathCover")
                )}
              />
              <RiskRatioTable
                dataSource={medicalBenefitCoverageData}
                columns={getRiskRatioTableColumns(
                  Locale.get("proposal.criticalIllnessMedBenefitCover")
                )}
              />
              <RiskRatioTable
                dataSource={tpdCoverageData}
                columns={getRiskRatioTableColumns(
                  Locale.get("proposal.totalPermanentDisabilityCover")
                )}
              />
              <RiskRatioTable
                dataSource={comatoseCoverageData}
                columns={getRiskRatioTableColumns(
                  Locale.get("proposal.comatoseCoverage")
                )}
              />
            </div>
          </SectionContainer>
        </div>
      ) : (
        <Empty
          style={{ paddingTop: 150 }}
          description={Locale.get("proposal.createProposalDesc")}
        >
          <Button type="primary" onClick={handleOnCreateProposalButtonClick}>
            {Locale.get("proposal.createProposal")}
          </Button>
        </Empty>
      )}
    </>
  );
};

Summary.defaultProps = {
  personalInfo: {},
  currentReporting: {},
};

export default Summary;
